import { useState, useEffect } from "react";
import { useTranslation } from "../context/LanguageContext";
import {
  Download,
  Key,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  ExternalLink,
} from "lucide-react";
import {
  getApiKey,
  generateApiKey,
  regenerateApiKey,
  deactivateApiKey,
  type ApiKey,
} from "../services/ApiKeyService";

export default function ExtensionPage() {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState<ApiKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    try {
      setLoading(true);
      const key = await getApiKey();
      setApiKey(key);
    } catch (error) {
      console.error("Error fetching API key:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    try {
      setGenerating(true);
      // Use regenerate if there's an existing key, generate if there isn't
      const newKey = apiKey ? await regenerateApiKey() : await generateApiKey();
      setApiKey(newKey);
    } catch (error) {
      console.error("Error generating API key:", error);
      alert(t("extension.generateFailed"));
    } finally {
      setGenerating(false);
    }
  };

  const handleDeactivateApiKey = async () => {
    if (!apiKey) return;

    const confirmed = window.confirm(t("extension.confirmDeactivate"));

    if (!confirmed) return;

    try {
      setDeactivating(true);
      await deactivateApiKey();
      setApiKey(null);
      alert(t("extension.keyDeactivated"));
    } catch (error) {
      console.error("Error deactivating API key:", error);
      alert("Failed to deactivate API key. Please try again.");
    } finally {
      setDeactivating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!apiKey?.apiKey) return;

    try {
      await navigator.clipboard.writeText(apiKey.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      alert(
        "Failed to copy to clipboard. Please select and copy the API key manually."
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return key;
    return (
      key.substring(0, 4) +
      "•".repeat(key.length - 8) +
      key.substring(key.length - 4)
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t("extension.title")}
        </h1>
        <p className="text-gray-600">{t("extension.description")}</p>
      </div>

      {/* Download Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center mb-4">
          <Download className="w-6 h-6 text-primary-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">
            {t("extension.downloadExtension")}
          </h2>
        </div>

        <div className="space-y-4">
          <p className="text-gray-600">{t("extension.downloadDescription")}</p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://chrome.google.com/webstore"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              {t("extension.visitChromeStore")}
            </a>

            <div className="text-sm text-gray-500 flex items-center">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
              {t("extension.comingSoon")}
            </div>
          </div>
        </div>
      </div>

      {/* API Key Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <Key className="w-6 h-6 text-primary-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">
            {t("extension.apiKeySetup")}
          </h2>
        </div>

        <div className="space-y-4">
          <p className="text-gray-600">{t("extension.apiKeyDescription")}</p>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : apiKey && apiKey.active ? (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  {t("extension.yourApiKey")}
                </span>
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {t("extension.active")}
                </span>
              </div>

              <div className="space-y-3 sm:space-y-0 mb-3">
                {/* Mobile layout - stacked */}
                <div className="sm:hidden">
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 font-mono text-sm overflow-x-auto mb-3">
                    <div className="min-w-0 whitespace-nowrap">
                      {showApiKey ? apiKey.apiKey : maskApiKey(apiKey.apiKey)}
                    </div>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onPointerDown={() => setShowApiKey(!showApiKey)}
                      className="flex-1 p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                      title={
                        showApiKey
                          ? t("extension.hideApiKey")
                          : t("extension.showApiKey")
                      }
                    >
                      {showApiKey ? (
                        <EyeOff className="w-4 h-4 mx-auto" />
                      ) : (
                        <Eye className="w-4 h-4 mx-auto" />
                      )}
                    </button>

                    <button
                      onPointerDown={copyToClipboard}
                      className="flex-1 p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                      title={t("extension.copyToClipboard")}
                    >
                      <Copy className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                </div>

                {/* Desktop layout - inline */}
                <div className="hidden sm:flex items-center space-x-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 font-mono text-sm overflow-x-auto">
                    <div className="min-w-0 whitespace-nowrap">
                      {showApiKey ? apiKey.apiKey : maskApiKey(apiKey.apiKey)}
                    </div>
                  </div>

                  <button
                    onPointerDown={() => setShowApiKey(!showApiKey)}
                    className="p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    title={
                      showApiKey
                        ? t("extension.hideApiKey")
                        : t("extension.showApiKey")
                    }
                  >
                    {showApiKey ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>

                  <button
                    onPointerDown={copyToClipboard}
                    className="p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {copied && (
                <div className="text-sm text-green-600 mb-2">
                  {t("extension.copiedToClipboard")}
                </div>
              )}

              <div className="text-xs text-gray-500 mb-4">
                Created: {formatDate(apiKey.created)}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onPointerDown={handleGenerateApiKey}
                  disabled={generating}
                  className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate Key
                    </>
                  )}
                </button>

                <button
                  onPointerDown={handleDeactivateApiKey}
                  disabled={deactivating}
                  className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deactivating
                    ? t("extension.deactivating")
                    : t("extension.deactivateKey")}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("extension.noApiKeyFound")}
              </h3>
              <p className="text-gray-600 mb-4">
                {t("extension.generateApiKeyDescription")}
              </p>

              <button
                onPointerDown={handleGenerateApiKey}
                disabled={generating}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    {t("extension.generating")}
                  </>
                ) : (
                  <>
                    <Key className="w-5 h-5 mr-2" />
                    {t("extension.generateApiKey")}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Instructions Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          {t("extension.setupInstructions")}
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li>{t("extension.instruction1")}</li>
          <li>{t("extension.instruction2")}</li>
          <li>{t("extension.instruction3")}</li>
          <li>{t("extension.instruction4")}</li>
          <li>{t("extension.instruction5")}</li>
        </ol>
      </div>
    </div>
  );
}
